<template>
  <Header :title="$t('newJob.title')" />
  <div v-if="readPermission" class="pageContainer">
    <div class="newJobContainer">
      <div class="uploadDivContainer">
        <div class="upload-section">
          <span class="pageLabel">{{ $t('newJob.upload.title') }}</span>
          <div class="uploadDivBorder" @click="triggerFileInput" @dragover.prevent="handleDragOver" @dragleave.prevent="handleDragLeave" @drop.prevent="handleDrop" :class="{ 'drag-over': isDragging }">
            <input type="file" ref="fileInput" style="display: none" @change="handleFileUpload" multiple :accept="allowedFileFormats.extensions" />
            <img :src="uploadIcon" alt="" />
            <p class="labelParagraph">
              <span class="subPageLabel">{{ $t('newJob.upload.drop_files') }}</span>
              <span class="clickUploadLabel">{{ $t('newJob.upload.click_upload') }}</span>
            </p>
            <p class="smallLabel">{{ $t('newJob.upload.file_types') }}: {{ allowedFileFormats.displayText }}</p>
            <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
          </div>
        </div>

        <div class="upload-section">
          <span class="pageLabel">{{ $t('newJob.import.title') }}</span>
          <div class="uploadDivBorder">
            <img :src="downloadIcon" alt="" />
            <p class="labelParagraph">
              <span class="subPageLabel">{{ $t('newJob.import.cloud_storage') }}</span>
              <span class="clickUploadLabel" @click="showVasionImportModal = true">{{ $t('newJob.import.click_import') }}</span>
            </p>
            <p class="smallLabel">{{ $t('newJob.import.file_types') }}</p>
          </div>
        </div>
      </div>

      <div class="documentSelectedDiv">
        <span class="pageLabel">{{ $t('newJob.documents.title') }}</span>
        <div class="documentDiv">
          <div class="classification-section" v-if="!shouldShowClassificationSection">
            <div class="classification-header">
              <span class="pageLabel">{{ $t('newJob.documents.classification.title') }}</span>
              <div v-if="!isTextract" class="auto-classification">
                <input type="checkbox" id="auto-classification" v-model="useAutoClassification" class="auto-classification-checkbox" />
                <label for="auto-classification">{{ $t('newJob.documents.classification.auto_classification') }}</label>
              </div>
            </div>
            <div class="classification-dropdowns" v-if="!useAutoClassification">
              <!-- Primary Classification - Hidden when Bedrock or Textract AI method is active -->
              <div class="categoryContainer" v-if="!isBedrock && !isTextract">
                <p class="categoryTitle">{{ $t('newJob.documents.classification.primary.title') }}</p>
                <select class="categorySelect" v-model="selectedCategory" @change="handleCategoryChange" :disabled="isLoadingCategories">
                  <option value="">{{ $t('newJob.documents.classification.primary.placeholder') }}</option>
                  <option v-for="category in categories" :key="category.id" :value="category.id">
                    {{ category.category_display_name || category.name }}
                  </option>
                </select>
                <div v-if="isLoadingCategories" class="loading-indicator">{{ $t('newJob.loading.categories') }}</div>
              </div>
              <!-- Sub-Category - Hidden when Bedrock or Textract AI method is active -->
              <div class="categoryContainer" v-if="!isBedrock && !isTextract">
                <p class="categoryTitle">{{ $t('newJob.documents.classification.sub_category.title') }}</p>
                <select class="categorySelect" v-model="selectedDepartment" @change="handleDepartmentChange" :disabled="!selectedCategory || isLoadingDepartments">
                  <option value="">{{ $t('newJob.documents.classification.sub_category.placeholder') }}</option>
                  <option v-for="department in departments" :key="department.id" :value="department.id">
                    {{ department.department_display_name || department.department_name }}
                  </option>
                </select>
                <div v-if="isLoadingDepartments" class="loading-indicator">{{ $t('newJob.loading.departments') }}</div>
              </div>
              <!-- Document Type - Always visible -->
              <div class="categoryContainer">
                <p class="categoryTitle">
                  {{ $t('newJob.documents.classification.document_type.title') }}
                </p>
                <select class="categorySelect" v-model="selectedDocumentType" :disabled="(!selectedDepartment && !isBedrock && !isTextract) || isLoadingDocumentTypes">
                  <template v-if="isLoadingDocumentTypes">
                    <option value="" disabled>{{ isBedrock || isTextract ? 'Loading document types...' : $t('newJob.loading.document_types') }}</option>
                  </template>
                  <template v-else>
                    <option value="">{{ $t('newJob.documents.classification.document_type.placeholder') }}</option>
                    <option v-for="type in isBedrock || isTextract ? documentTypes.filter((dt) => dt.is_published) : documentTypes" :key="type.id" :value="type.id">
                      {{ type.document_type_display_name }}
                    </option>
                  </template>
                </select>
                <div v-if="isLoadingDocumentTypes" class="loading-indicator">
                  {{ isBedrock || isTextract ? 'Loading all document types...' : $t('newJob.loading.document_types') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Default Values Section -->
          <div class="default-values-section" v-if="!shouldShowClassificationSection && !useAutoClassification && selectedDocumentType && (isLoadingDocumentTypesFields || documentTypesFields.length > 0)">
            <div class="default-values-header">
              <span class="pageLabel">Select Default Values For Vasion Fields</span>
            </div>

            <!-- Loading State -->
            <div v-if="isLoadingDocumentTypesFields" class="default-values-loading">
              <div class="loading-indicator">Loading default values...</div>
            </div>

            <!-- Default Values Dropdowns -->
            <div v-else class="default-values-dropdowns">
              <div class="defaultValueContainer" v-for="field in documentTypesFields" :key="field.id">
                <p class="defaultValueTitle">{{ field.vasion_field_name }}</p>
                <select class="defaultValueSelect" :value="field.selected_value || ''" @change="handleDefaultValueChange(field, $event.target.value)">
                  <option value="">Select {{ field.vasion_field_name }}</option>
                  <option v-for="option in parseDropdownValues(field.vasion_drop_down_values)" :key="option" :value="option">
                    {{ option }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Show message if no files are selected -->
          <div class="uploadedFiles" v-if="uploadedFiles.length === 0">
            <img :src="documentSelected" alt="" height="150px" />
            <span class="noDocumentLabel">{{ $t('newJob.documents.no_documents.title') }}</span>
            <p class="uploadLabel">{{ $t('newJob.documents.no_documents.message') }}</p>
          </div>

          <!-- Show the files if at least one is uploaded -->
          <div v-else class="files-wrapper">
            <div class="files-container">
              <div v-for="(file, index) in uploadedFiles" :key="index" class="file-item">
                <div class="file-info">
                  <img :src="pdfIcon" alt="PDF Icon" class="file-icon" />
                  <div class="file-group">
                    <span class="file-name">
                      {{ file.name }}
                      <span class="file-tag" :class="{ vasion: file.isVasion }">
                        {{ file.isVasion ? $t('newJob.documents.file.vasion') : $t('newJob.documents.file.local') }}
                      </span>
                    </span>

                    <span class="file-size">
                      <template v-if="!file.isVasion">
                        {{ formatFileSize(file.size) }}
                      </template>
                      <template v-else> {{ $t('newJob.documents.file.size_not_available') }} </template>
                    </span>
                  </div>
                  <img @click="removeFile(index)" class="remove-file" :src="closeIcon" alt="" />
                </div>
              </div>
            </div>
            <div v-if="writePermission" class="button-container">
              <button class="cancel-button" @click="cancelUpload">{{ $t('newJob.buttons.cancel') }}</button>
              <button class="upload-button" @click="uploadDocuments" :disabled="isUploading">{{ isUploading ? $t('newJob.buttons.uploading') : $t('newJob.buttons.upload') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="!readPermission">
    <PermissionAlert :message="$t('newJob.no_permission')" />
  </div>
  <div v-if="Object.keys(uploadProgress).length > 0" class="upload-progress">
    <div v-for="(progress, fileName) in uploadProgress" :key="fileName">
      <span>{{ fileName }}: {{ progress }}%</span>
      <div class="progress-bar" :style="{ width: progress + '%' }"></div>
    </div>
  </div>
  <VasionImportModal v-if="showVasionImportModal" :show="showVasionImportModal" @close="showVasionImportModal = false" @import="handleVasionImport" />
</template>

<script>
import Header from '../../components/Header.vue'
import { post, get, put } from '../../utils/api.js'
import VasionImportModal from '../../components/VasionImportModal/VasionImportModal.vue'
import { showToast } from '../../utils/toast.js'
import PermissionAlert from '../../components/PermissionAlert.vue'
import { isAiMethodBedrock, isAiMethodTextract } from '@/utils/tenantUtils.js'
import uploadIcon from '@/assets/uploadicon.png'
import downloadIcon from '@/assets/downloadicon.png'
import documentSelected from '@/assets/documentSelected.png'
import pdfIcon from '@/assets/pdficon.png'
import closeIcon from '@/assets/close.png'
import * as pdfjsLib from 'pdfjs-dist'
import * as UTIF from 'utif'

export default {
  components: {
    Header,
    VasionImportModal,
    PermissionAlert,
  },

  data() {
    return {
      uploadedFiles: [],
      uploadProgress: {},
      jobData: null,
      isDragging: false,
      errorMessage: '',
      showVasionImportModal: false,
      useAutoClassification: true,
      selectedCategory: '',
      selectedDepartment: '',
      selectedDocumentType: '',
      selectedDocumentTypeUploadedFilePagesCount: null,
      selectedDocumentTypeSplitOnCode: null,
      splitByPageCount: null,
      categories: [],
      departments: [],
      documentTypes: [],
      documentTypesFields: [],
      isUploading: false,
      uploadIcon,
      downloadIcon,
      documentSelected,
      pdfIcon,
      closeIcon,
      readPermission: false,
      writePermission: false,
      isLoadingCategories: false,
      isLoadingDepartments: false,
      isLoadingDocumentTypes: false,
      isLoadingDocumentTypesFields: false,
      selectedTenantUsesBedrock: false,
    }
  },

  mounted() {
    document.title = 'New Job'
    this.checkPermissions()

    // For Textract, always force manual classification
    if (isAiMethodTextract()) {
      this.useAutoClassification = false
    }

    // Initialize PDF worker for page count validation
    this.configurePdfWorker()

    this.initializeClassificationFromAPI()
  },

  computed: {
    shouldShowClassificationSection() {
      return false
    },
    isBedrock() {
      return isAiMethodBedrock()
    },
    isTextract() {
      return isAiMethodTextract()
    },
    // Get the selected document type's Textract analysis type from bda_blueprint
    selectedTextractAnalysisType() {
      if (!this.isTextract || !this.selectedDocumentType) {
        return null
      }
      const selectedDocType = this.documentTypes.find((dt) => dt.id === this.selectedDocumentType)
      return selectedDocType?.bda_blueprint || null
    },
    // Computed property for allowed file formats based on Textract analysis type
    allowedFileFormats() {
      if (!this.isTextract) {
        // Default: all formats allowed for Bedrock or non-Textract flows
        return {
          mimeTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff'],
          extensions: '.pdf,.png,.jpg,.jpeg,.tiff,.tif,application/pdf,image/png,image/jpeg,image/tiff',
          displayText: 'PDF, PNG, JPEG, TIFF',
        }
      }

      // If Textract but no document type selected yet, allow all formats by default
      if (!this.selectedTextractAnalysisType) {
        return {
          mimeTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff'],
          extensions: '.pdf,.png,.jpg,.jpeg,.tiff,.tif,application/pdf,image/png,image/jpeg,image/tiff',
          displayText: 'PDF, PNG, JPEG, TIFF',
        }
      }

      // Textract-specific format restrictions based on analysis type
      switch (this.selectedTextractAnalysisType) {
        case 'AnalyzeDocument':
          // Async API - supports multipage PDF/TIFF
          return {
            mimeTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff'],
            extensions: '.pdf,.png,.jpg,.jpeg,.tiff,.tif,application/pdf,image/png,image/jpeg,image/tiff',
            displayText: 'PDF, PNG, JPEG, TIFF ',
          }
        case 'AnalyzeExpense':
          // Async API - supports multipage PDF/TIFF
          return {
            mimeTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff'],
            extensions: '.pdf,.png,.jpg,.jpeg,.tiff,.tif,application/pdf,image/png,image/jpeg,image/tiff',
            displayText: 'PDF, PNG, JPEG, TIFF ',
          }
        case 'AnalyzeID':
          // Synchronous API only - single page documents only (AWS limitation)
          return {
            mimeTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff'],
            extensions: '.pdf,.png,.jpg,.jpeg,.tiff,.tif,application/pdf,image/png,image/jpeg,image/tiff',
            displayText: 'PDF, PNG, JPEG, TIFF (single page only)',
          }
        case 'AnalyzeLending':
          // Async API - supports multipage PDF/TIFF
          return {
            mimeTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff'],
            extensions: '.pdf,.png,.jpg,.jpeg,.tiff,.tif,application/pdf,image/png,image/jpeg,image/tiff',
            displayText: 'PDF, PNG, JPEG, TIFF ',
          }
        default:
          return {
            mimeTypes: ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff'],
            extensions: '.pdf,.png,.jpg,.jpeg,.tiff,.tif,application/pdf,image/png,image/jpeg,image/tiff',
            displayText: 'PDF, PNG, JPEG, TIFF',
          }
      }
    },
  },

  methods: {
    async initializeClassificationFromAPI() {
      try {
        // Get user data from API
        const userData = JSON.parse(localStorage.getItem('UserData'))
        const userId = userData?.id
        if (!userId) {
          // If Bedrock or Textract is enabled, fetch all document types directly
          if (isAiMethodBedrock() || isAiMethodTextract()) {
            await this.fetchAllDocumentTypes()
          } else {
            await this.fetchCategories()
          }
          return
        }

        const response = await get(`/v1/users/${userId}`)
        if (response?.data?.code === 200) {
          const user = response.data.data

          // Set auto classification based on user preference
          this.useAutoClassification = !user.select_document_classification

          // If manual classification is enabled, restore the values from API
          if (!this.useAutoClassification) {
            if (isAiMethodBedrock() || isAiMethodTextract()) {
              // For Bedrock or Textract, only fetch all document types and restore document type selection
              await this.fetchAllDocumentTypes()
              const documentTypeToRestore = user.tenant_document_type_id || ''
              this.selectedDocumentType = documentTypeToRestore

              // Validate and clean up invalid document type ID
              await this.validateAndCleanupInvalidIds(user, userId)
            } else {
              // For non-Bedrock, use the existing logic
              if (user.tenant_primary_classification_id) {
                // Store the values we want to restore
                const categoryToRestore = user.tenant_primary_classification_id
                const departmentToRestore = user.tenant_department_id || ''
                const documentTypeToRestore = user.tenant_document_type_id || ''

                // Fetch categories first
                await this.fetchCategories()

                // Restore category selection
                this.selectedCategory = categoryToRestore

                // If we have a selected category, fetch departments
                if (this.selectedCategory) {
                  const categoryExists = this.categories.find((c) => c.id === this.selectedCategory)
                  if (!categoryExists) {
                    // Category doesn't exist, clear it and update user
                    this.selectedCategory = ''
                    await this.updateUserClassificationIds(userId, null, null, null)
                  } else {
                    const masterCategoryId = categoryExists.category_id
                    await this.fetchDepartmentsForCategory(masterCategoryId)

                    // Restore department selection
                    this.selectedDepartment = departmentToRestore

                    // Validate department exists
                    if (this.selectedDepartment) {
                      const departmentExists = this.departments.find((d) => d.id === this.selectedDepartment)
                      if (!departmentExists) {
                        // Department doesn't exist, clear it and update user
                        this.selectedDepartment = ''
                        this.selectedDocumentType = ''
                        await this.updateUserClassificationIds(userId, this.selectedCategory, null, null)
                      } else {
                        // If we have a valid selected department, fetch document types
                        const masterDepartmentId = departmentExists.department_id
                        await this.fetchDocumentTypesForDepartment(masterCategoryId, masterDepartmentId)

                        // Restore document type selection
                        this.selectedDocumentType = documentTypeToRestore

                        // Validate document type exists
                        if (this.selectedDocumentType) {
                          const documentTypeExists = this.documentTypes.find((dt) => dt.id === this.selectedDocumentType)
                          if (!documentTypeExists) {
                            // Document type doesn't exist, clear it and update user
                            this.selectedDocumentType = ''
                            await this.updateUserClassificationIds(userId, this.selectedCategory, this.selectedDepartment, null)
                          }
                        }
                      }
                    }
                  }
                }
              } else {
                // Just fetch categories for manual classification
                await this.fetchCategories()
              }
            }
          } else {
            // For auto classification, still fetch categories unless it's Bedrock or Textract
            if (isAiMethodBedrock() || isAiMethodTextract()) {
              await this.fetchAllDocumentTypes()
            } else {
              await this.fetchCategories()
            }
          }
        } else {
          // Fallback logic
          if (isAiMethodBedrock() || isAiMethodTextract()) {
            await this.fetchAllDocumentTypes()
          } else {
            await this.fetchCategories()
          }
        }
      } catch (error) {
        // Fallback logic
        if (isAiMethodBedrock() || isAiMethodTextract()) {
          await this.fetchAllDocumentTypes()
        } else {
          await this.fetchCategories()
        }
      }
    },

    // Validate and cleanup invalid IDs for Bedrock mode
    async validateAndCleanupInvalidIds(user, userId) {
      // Validate document type ID for Bedrock
      if (this.selectedDocumentType) {
        const documentTypeExists = this.documentTypes.find((dt) => dt.id === this.selectedDocumentType)
        if (!documentTypeExists) {
          // Document type doesn't exist, clear it and update user
          this.selectedDocumentType = ''
          await this.updateUserClassificationIds(userId, null, null, null)
        }
      }
    },

    // Helper method to update user classification IDs in the API
    async updateUserClassificationIds(userId, categoryId, departmentId, documentTypeId) {
      try {
        const userData = JSON.parse(localStorage.getItem('UserData'))
        const updatePayload = {
          ...userData,
          tenant_primary_classification_id: categoryId,
          tenant_department_id: departmentId,
          tenant_document_type_id: documentTypeId,
        }

        await put(`/v1/users/${userId}`, updatePayload)

        // Update localStorage with the new values
        const updatedUserData = { ...userData }
        updatedUserData.tenant_primary_classification_id = categoryId
        updatedUserData.tenant_department_id = departmentId
        updatedUserData.tenant_document_type_id = documentTypeId
        localStorage.setItem('UserData', JSON.stringify(updatedUserData))
      } catch (error) {
        console.error('Failed to update user classification IDs:', error)
      }
    },

    async saveClassificationToAPI() {
      try {
        const userData = JSON.parse(localStorage.getItem('UserData'))
        const userId = userData?.id
        if (!userId) return

        const updatePayload = {
          ...userData,
          select_document_classification: !this.useAutoClassification,
        }

        // Add classification values only if manual classification is enabled
        if (!this.useAutoClassification) {
          if (isAiMethodBedrock() || isAiMethodTextract()) {
            // For Bedrock or Textract, only save document type
            updatePayload.tenant_primary_classification_id = null
            updatePayload.tenant_department_id = null
            updatePayload.tenant_document_type_id = this.selectedDocumentType || null
          } else {
            // For non-Bedrock, save all classification values
            updatePayload.tenant_primary_classification_id = this.selectedCategory || null
            updatePayload.tenant_department_id = this.selectedDepartment || null
            updatePayload.tenant_document_type_id = this.selectedDocumentType || null
          }
        } else {
          // Clear classification values when auto classification is enabled
          updatePayload.tenant_primary_classification_id = null
          updatePayload.tenant_department_id = null
          updatePayload.tenant_document_type_id = null
        }

        await put(`/v1/users/${userId}`, updatePayload)
      } catch (error) {
        console.error('Failed to save classification to API:', error)
      }
    },

    checkPermissions() {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
      this.readPermission = permissions.some((p) => p.object_name === 'NEW_JOB_RD' && p.is_active)
      this.writePermission = permissions.some((p) => p.object_name === 'NEW_JOB_WR' && p.is_active)
    },
    triggerFileInput() {
      // Open file dialog
      this.$refs.fileInput.click()
    },

    handleDragOver(e) {
      this.isDragging = true
      e.dataTransfer.dropEffect = 'copy'
    },

    handleDragLeave() {
      this.isDragging = false
    },

    handleDrop(e) {
      this.isDragging = false
      const items = Array.from(e.dataTransfer.items)
      const files = []

      // Process each item in the dropped folder
      const processItems = async () => {
        for (const item of items) {
          if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry()
            if (entry.isDirectory) {
              // If it's a directory, get all files recursively
              await this.processDirectory(entry, files)
            } else if (entry.isFile) {
              // If it's a file, add it directly
              const file = item.getAsFile()
              if (this.allowedFileFormats.mimeTypes.includes(file.type)) {
                files.push(file)
              }
            }
          }
        }
        await this.validateAndAddFiles(files)
      }

      processItems()
    },

    // Helper method to process directory recursively
    processDirectory(entry, files) {
      return new Promise((resolve) => {
        const reader = entry.createReader()
        reader.readEntries((entries) => {
          const promises = entries.map((entry) => {
            if (entry.isDirectory) {
              return this.processDirectory(entry, files)
            } else if (entry.isFile) {
              return new Promise((resolveFile) => {
                entry.file((file) => {
                  if (this.allowedFileFormats.mimeTypes.includes(file.type)) {
                    files.push(file)
                  }
                  resolveFile()
                })
              })
            }
          })
          Promise.all(promises).then(resolve)
        })
      })
    },

    async validateExistingFilesForAnalyzeID() {
      const invalidFiles = []
      const filesToKeep = []

      const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/tiff']
      // Special characters that are not allowed in file names
      const invalidChars = /[<>:"/\\|?*@#$%^&*]/

      for (const file of this.uploadedFiles) {
        let isValid = true // Initialize isValid for each file

        // Check for special characters in file name
        if (invalidChars.test(file.name)) {
          this.errorMessage = `File "${file.name}" contains special characters that are not allowed. Please rename the file and try again.`
          setTimeout(() => {
            this.errorMessage = ''
          }, 3000) // Clear error after 3 seconds
          continue
        }

        if (allowedTypes.includes(file.type)) {
          this.uploadedFiles.push(file)
        } else {
          this.errorMessage = `File "${file.name}" is not a supported file type. Only PDF, PNG, JPEG, and TIFF files are allowed.`
          setTimeout(() => {
            this.errorMessage = ''
          }, 3000) // Clear error after 3 seconds
          isValid = false
        }

        // Check PDF page count
        if (file.type === 'application/pdf') {
          try {
            const pageCount = await this.getPdfPageCount(file)
            if (pageCount > 1) {
              invalidFiles.push({ file, reason: `multipage PDF (${pageCount} pages)` })
              isValid = false
            }
          } catch (error) {
            console.error('Error validating existing PDF:', error)
            invalidFiles.push({ file, reason: 'PDF validation error' })
            isValid = false
          }
        }

        // Check TIFF page count
        if (file.type === 'image/tiff' && isValid) {
          try {
            const pageCount = await this.getTiffPageCount(file)
            if (pageCount > 1) {
              invalidFiles.push({ file, reason: `multipage TIFF (${pageCount} pages)` })
              isValid = false
            }
          } catch (error) {
            console.error('Error validating existing TIFF:', error)
            invalidFiles.push({ file, reason: 'TIFF validation error' })
            isValid = false
          }
        }

        if (isValid) {
          filesToKeep.push(file)
        }
      }

      // Update uploaded files to only keep valid ones
      this.uploadedFiles = filesToKeep

      // Show toast if any files were removed
      if (invalidFiles.length > 0) {
        const fileNames = invalidFiles.map((item) => `"${item.file.name}" (${item.reason})`).join(', ')
        showToast({
          title: 'Files Removed',
          message: `AnalyzeID only supports single-page PDFs, TIFFs, PNGs, and JPEGs. The following files were removed: ${fileNames}`,
          type: 'warning',
        })
      }
    },

    async validateAndAddFiles(files) {
      const allowedTypes = this.allowedFileFormats.mimeTypes
      // Special characters that are not allowed in file names
      const invalidChars = /[<>:"/\\|?*@#$%^&*]/

      for (const file of files) {
        // Check for special characters in file name
        if (invalidChars.test(file.name)) {
          showToast({
            title: 'Invalid File Name',
            message: `File "${file.name}" contains special characters that are not allowed. Please rename the file and try again.`,
            type: 'error',
          })
          continue
        }

        if (!allowedTypes.includes(file.type)) {
          const formatText = this.isTextract && this.selectedTextractAnalysisType ? `For ${this.selectedTextractAnalysisType}, only ${this.allowedFileFormats.displayText} are allowed.` : `Only ${this.allowedFileFormats.displayText} are allowed.`
          showToast({
            title: 'Invalid File Type',
            message: `File "${file.name}" is not supported. ${formatText}`,
            type: 'error',
          })
          continue
        }

        // Validate page count for Bedrock document types
        const pageCountValidation = await this.validatePageCountForDocumentType(file)
        if (!pageCountValidation.isValid) {
          showToast({
            title: 'Error : ',
            message: pageCountValidation.errorMessage,
            type: 'error',
          })
          continue
        }
        // For AnalyzeID, validate single-page requirement for PDF/TIFF
        if (this.isTextract && this.selectedTextractAnalysisType === 'AnalyzeID') {
          if (file.type === 'application/pdf') {
            try {
              const pageCount = await this.getPdfPageCount(file)
              if (pageCount > 1) {
                showToast({
                  title: 'Multipage PDF Not Supported',
                  message: `AnalyzeID only supports single-page PDFs. "${file.name}" has ${pageCount} pages. Please upload a single-page PDF or use PNG/JPEG format.`,
                  type: 'error',
                })
                continue
              }
            } catch (error) {
              console.error('Error checking PDF page count for ' + file.name + ':', error)
              const errorMsg = error.message || error.toString()
              showToast({
                title: 'PDF Validation Error',
                message: `Cannot validate PDF "${file.name}". ${errorMsg.includes('Invalid PDF') ? 'The file may be corrupted.' : 'Please try again or use PNG/JPEG format.'}`,
                type: 'error',
              })
              continue
            }
          } else if (file.type === 'image/tiff') {
            try {
              const pageCount = await this.getTiffPageCount(file)
              if (pageCount > 1) {
                showToast({
                  title: 'Multipage TIFF Not Supported',
                  message: `AnalyzeID only supports single-page TIFFs. "${file.name}" has ${pageCount} pages. Please upload a single-page TIFF or use PNG/JPEG format.`,
                  type: 'error',
                })
                continue
              }
            } catch (error) {
              console.error('Error checking TIFF page count for ' + file.name + ':', error)
              showToast({
                title: 'TIFF Validation Error',
                message: `Cannot validate TIFF "${file.name}". Please try again or use PNG/JPEG format.`,
                type: 'error',
              })
              continue
            }
          }
        }

        this.uploadedFiles.push(file)
      }
    },

    configurePdfWorker() {
      const version = pdfjsLib.version
      const workerUrls = [`https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.js`, `https://cdn.jsdelivr.net/npm/pdfjs-dist@${version}/build/pdf.worker.min.js`, `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`]

      // Try the first URL
      pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrls[0]
    },

    /**
     * Validates page count for uploaded document against trained document type
     * @param {File} file - The file to validate
     * @returns {Promise<{isValid: boolean, errorMessage?: string}>} Validation result
     */
    async validatePageCountForDocumentType(file) {
      // Only validate for Bedrock AI method and when document type is selected
      if (!this.isBedrock || !this.selectedDocumentType || file.type !== 'application/pdf') {
        return { isValid: true }
      }

      try {
        const pageCount = await this.getPdfPageCount(file)
        const training = Number(this.selectedDocumentTypeUploadedFilePagesCount)
        const splitByPageCount = Number(this.splitByPageCount)
        const isCodeSplit = this.selectedDocumentTypeSplitOnCode

        // Only validate if not using code splitting and split by page count is not configured
        if (!isCodeSplit && splitByPageCount === 0) {
          if (pageCount > training) {
            return {
              isValid: false,
              errorMessage: `Number of pages in uploaded document (${pageCount}) is more than the trained document (${training}). Kindly check the selected document type training or split setting to ensure appropriate matching.`,
            }
          }
        }

        return { isValid: true }
      } catch (error) {
        console.error('Error validating page count:', error)
        // If we can't validate, allow the file but log the error
        return { isValid: true }
      }
    },

    getPdfPageCount(file) {
      return new Promise((resolve, reject) => {
        // Force reconfigure worker before processing (same as Uploads.vue)
        this.configurePdfWorker()
        const fileReader = new FileReader()
        fileReader.onload = async (e) => {
          try {
            const typedArray = new Uint8Array(e.target.result)
            // Explicitly set worker source right before getDocument
            const version = pdfjsLib.version
            pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${version}/build/pdf.worker.min.js`

            const pdf = await pdfjsLib.getDocument({
              data: typedArray,
              // Try disabling worker as fallback
              useWorkerFetch: false,
              isEvalSupported: false,
            }).promise
            resolve(pdf.numPages)
          } catch (error) {
            reject(error)
          }
        }
        fileReader.onerror = (e) => {
          reject(e)
        }
        fileReader.readAsArrayBuffer(file)
      })
    },

    getTiffPageCount(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          try {
            const ifds = UTIF.decode(reader.result)
            resolve(ifds.length)
          } catch (e) {
            reject(e.message)
          }
        }
        reader.onerror = () => reject('Error reading file')
        reader.readAsArrayBuffer(file)
      })
    },

    async handleFileUpload(event) {
      const files = Array.from(event.target.files)
      // Check if it's a folder upload (webkitdirectory)
      if (event.target.webkitdirectory) {
        // Filter only allowed files from the folder
        const allowedFiles = files.filter((file) => {
          return this.allowedFileFormats.mimeTypes.includes(file.type)
        })
        await this.validateAndAddFiles(allowedFiles)
      } else {
        // Handle single file uploads
        await this.validateAndAddFiles(files)
      }
      event.target.value = '' // Reset input
    },

    removeFile(index) {
      this.uploadedFiles.splice(index, 1)
    },

    cancelUpload() {
      // Clear all selected files
      this.uploadedFiles = []
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
      const i = parseInt(Math.floor(Math.log(bytes) / Math.log(k)))
      return Math.round(bytes / Math.pow(k, i)) + ' ' + sizes[i]
    },
    async uploadDocuments() {
      try {
        // Check if more than 200 documents are uploaded
        if (this.uploadedFiles.length > 200) {
          showToast({
            title: this.$t('newJob.messages.error.title'),
            message: this.$t('newJob.messages.error.too_many_documents'),
            type: 'error',
          })
          return
        }

        // Add validation for manual classification
        if (!this.useAutoClassification) {
          // For Bedrock or Textract AI method, only validate Document Type
          if (isAiMethodBedrock() || isAiMethodTextract()) {
            if (!this.selectedDocumentType) {
              showToast({
                title: this.$t('newJob.messages.error.title'),
                message: this.$t('newJob.messages.error.document_type_required'),
                type: 'error',
              })
              return
            }
          } else {
            // For non-Bedrock methods, validate all three fields
            if (!this.selectedCategory) {
              showToast({
                title: this.$t('newJob.messages.error.title'),
                message: this.$t('newJob.messages.error.primary_classification_required'),
                type: 'error',
              })
              return
            }
            if (!this.selectedDepartment) {
              showToast({
                title: this.$t('newJob.messages.error.title'),
                message: this.$t('newJob.messages.error.sub_category_required'),
                type: 'error',
              })
              return
            }
            if (!this.selectedDocumentType) {
              showToast({
                title: this.$t('newJob.messages.error.title'),
                message: this.$t('newJob.messages.error.document_type_required'),
                type: 'error',
              })
              return
            }
          }
        }

        // Validate page count for all uploaded PDF files BEFORE any API calls
        if (this.isBedrock && this.selectedDocumentType) {
          const pdfFiles = this.uploadedFiles.filter((file) => file.type === 'application/pdf' && !file.isVasion)
          for (const file of pdfFiles) {
            const validation = await this.validatePageCountForDocumentType(file)
            if (!validation.isValid) {
              showToast({
                title: this.$t('newJob.messages.error.title'),
                message: `${file.name}: ${validation.errorMessage}`,
                type: 'error',
              })
              return
            }
          }
        }

        this.isUploading = true // start uploading
        // Step 1: Get tenant ID
        const userData = JSON.parse(localStorage.getItem('UserData'))
        const tenantId = userData?.tenant_id
        if (!tenantId) throw new Error('Tenant ID not found')

        // Step 2: Prepare job payload
        const jobPayload = { tenant_id: tenantId }
        if (!this.useAutoClassification) {
          let defaultValues = null
          if (this.documentTypesFields?.length) {
            defaultValues = this.documentTypesFields.map((field) => ({
              id: field.id,
              default_value_required: field.default_value_required,
              selected_value: field.selected_value,
            }))
          }

          if (isAiMethodBedrock() || isAiMethodTextract()) {
            // For Bedrock or Textract, only include document type and skip category/department
            Object.assign(jobPayload, {
              is_user_selected_classification: true,
              user_selected_primary_classification: null, // Not required for Bedrock/Textract
              user_selected_sub_category: null, // Not required for Bedrock/Textract
              user_selected_document_type: this.selectedDocumentType,
              default_values: JSON.stringify(defaultValues),
            })
          } else {
            // For non-Bedrock, include all classification fields
            Object.assign(jobPayload, {
              is_user_selected_classification: true,
              user_selected_primary_classification: this.selectedCategory,
              user_selected_sub_category: this.selectedDepartment,
              user_selected_document_type: this.selectedDocumentType,
              default_values: JSON.stringify(defaultValues),
            })
          }
        }

        // Step 3: Separate local and Vasion files
        const localFiles = this.uploadedFiles.filter((file) => !file.isVasion)
        const vasionFiles = this.uploadedFiles.filter((file) => file.isVasion)

        const jobRes = await post(`/v1/mx-jobs`, jobPayload)
        const jobId = jobRes?.data?.id
        if (!jobId) throw new Error('Failed to create job')

        // Step 4: Handle Vasion file upload
        if (vasionFiles.length > 0) {
          const docIds = vasionFiles.map((file) => file.sourceTypeId)
          const uploadRes = await post(`/v1/mx-jobs/upload-docs?tenantId=${tenantId}&jobId=${jobId}`, { docIds })
          localStorage.setItem('vasionUploadResult', JSON.stringify(uploadRes.data))
        }

        // Step 5: Handle local file upload - Use IndexedDB for large file storage
        if (localFiles.length > 0) {
          const fileNames = localFiles.map((file) => file.name)
          const presignedRes = await post('/v1/uploads/presigned-url', {
            mxJobId: jobId,
            fileNames,
          })

          // Store files in IndexedDB to avoid localStorage size limits
          try {
            await this.storeFilesInIndexedDB(localFiles, jobId)
          } catch (error) {
            showToast({
              title: this.$t('newJob.messages.error.title'),
              message: this.$t('newJob.messages.error.storage_failed'),
              type: 'error',
            })
            throw error
          }

          const uploadData = {
            jobId,
            timestamp: new Date().getTime(),
            files: localFiles.map((file, index) => {
              const presigned = presignedRes.data[index]
              if (!presigned) throw new Error(`Presigned URL missing for: ${file.name}`)

              return {
                name: file.name,
                size: file.size,
                type: file.type,
                fileId: presigned.id,
                s3_path: presigned.s3_path,
                mx_job_id: presigned.mx_job_id,
                tenant_id: presigned.tenant_id,
                automation: presigned.automation,
                progress: 0,
                status: 'Pending',
                // Store reference to file in IndexedDB
                fileKey: `${jobId}_${file.name}`,
              }
            }),
          }

          localStorage.setItem('uploadStatusData', JSON.stringify(uploadData))
        }

        // Step 6: Update user classification preferences
        await this.saveClassificationToAPI()

        // Step 8: Redirect to upload status
        const tenantName = localStorage.getItem('url_name')
        this.$router.push(`/${tenantName}/upload-status`)
      } catch (err) {
        showToast({
          title: this.$t('newJob.messages.error.title'),
          message: err.message || this.$t('newJob.messages.error.upload_failed'),
          type: 'error',
        })
      } finally {
        this.isUploading = false // done uploading
      }
    },

    handleVasionImport(files) {
      const importedFiles = files.map((file) => ({
        name: file.DisplayName,
        type: 'application/pdf',
        isVasion: true,
        sourceTypeId: file.TypeId,
      }))

      this.uploadedFiles = [...this.uploadedFiles, ...importedFiles]
    },

    async fetchCategories() {
      try {
        this.isLoadingCategories = true
        const userData = JSON.parse(localStorage.getItem('UserData'))
        const tenantId = userData?.tenant_id
        const response = await get(`/v1/tenant-categories?page=1&pageSize=1000&tenantId=${tenantId}`)
        if (response?.data?.code === 200) {
          this.categories = response.data.data.data.filter((cat) => cat.is_active === true)
        }
      } catch (err) {
        showToast({
          title: this.$t('newJob.messages.error.title'),
          message: this.$t('newJob.messages.error.fetch_categories_failed'),
          type: 'error',
        })
      } finally {
        this.isLoadingCategories = false
      }
    },

    async fetchDepartmentsForCategory(categoryId) {
      try {
        this.isLoadingDepartments = true
        const userData = JSON.parse(localStorage.getItem('UserData'))
        const tenantId = userData?.tenant_id
        const response = await get(`/v1/tenant-departments?tenantId=${tenantId}&categoryId=${categoryId}&page=1&pageSize=1000`)
        if (response?.data?.code === 200) {
          this.departments = response.data.data.data.filter((dept) => dept.is_active === true)
        }
      } catch (err) {
        showToast({
          title: this.$t('newJob.messages.error.title'),
          message: this.$t('newJob.messages.error.fetch_departments_failed'),
          type: 'error',
        })
      } finally {
        this.isLoadingDepartments = false
      }
    },

    async fetchDocumentTypesForDepartment(categoryId, departmentId) {
      try {
        this.isLoadingDocumentTypes = true
        const userData = JSON.parse(localStorage.getItem('UserData'))
        const tenantId = userData?.tenant_id
        const response = await get(`/v1/tenant-document-types?page=1&pageSize=1000&categoryId=${categoryId}&departmentId=${departmentId}&tenantId=${tenantId}&isDeleted=false`)
        if (response?.data?.code === 200) {
          this.documentTypes = response?.data?.data?.data?.filter((docType) => docType.is_active === true)
        }
      } catch (err) {
        showToast({
          title: this.$t('newJob.messages.error.title'),
          message: this.$t('newJob.messages.error.fetch_document_types_failed'),
          type: 'error',
        })
      } finally {
        this.isLoadingDocumentTypes = false
      }
    },

    async fetchAllDocumentTypes() {
      try {
        this.isLoadingDocumentTypes = true
        const userData = JSON.parse(localStorage.getItem('UserData'))
        const tenantId = userData?.tenant_id
        const response = await get(`/v1/tenant-document-types?page=1&pageSize=1000&tenantId=${tenantId}&isDeleted=false`)
        if (response?.data?.code === 200) {
          this.documentTypes = response?.data?.data?.data?.filter((docType) => docType.is_active === true)
        }
      } catch (err) {
        showToast({
          title: this.$t('newJob.messages.error.title'),
          message: this.$t('newJob.messages.error.fetch_document_types_failed'),
          type: 'error',
        })
      } finally {
        this.isLoadingDocumentTypes = false
      }
    },

    async fetchDocumentFields(documentTypeId = null) {
      try {
        this.isLoadingDocumentTypesFields = true
        const userData = JSON.parse(localStorage.getItem('UserData'))
        // Use passed documentTypeId or fall back to userData
        const docTypeId = documentTypeId || userData?.tenant_document_type_id

        if (!docTypeId) {
          this.documentTypesFields = []
          return
        }

        const response = await get(`/v1/tenant-document-type-fields/default-value-fields?tenantDocumentTypeId=${docTypeId}`)
        if (response?.data?.code === 200) {
          this.documentTypesFields = response.data?.data?.filter((docType) => docType.is_active === true)
        }
      } catch (err) {
        showToast({
          title: this.$t('newJob.messages.error.title'),
          message: this.$t('newJob.messages.error.fetch_document_types_failed'),
          type: 'error',
        })
      } finally {
        this.isLoadingDocumentTypesFields = false
      }
    },
    async storeFilesInIndexedDB(files, jobId) {
      // Check if IndexedDB is supported
      if (!window.indexedDB) {
        throw new Error('IndexedDB is not supported in this browser')
      }

      // Convert all files to base64 first (before any IndexedDB operations)
      const fileDataPromises = files.map(async (file) => {
        const fileData = await this.fileToBase64(file)
        return {
          key: `${jobId}_${file.name}`,
          data: fileData,
          name: file.name,
          size: file.size,
          type: file.type,
          timestamp: Date.now(),
        }
      })

      // Wait for all files to be converted
      const fileDataArray = await Promise.all(fileDataPromises)

      // Now store all files in IndexedDB in a single transaction
      return new Promise((resolve, reject) => {
        const request = indexedDB.open('FileStorage', 1)

        request.onerror = () => {
          reject(new Error('Failed to open IndexedDB: ' + request.error.message))
        }

        request.onsuccess = () => {
          const db = request.result
          const transaction = db.transaction(['files'], 'readwrite')
          const store = transaction.objectStore('files')

          let completed = 0
          const total = fileDataArray.length

          // Store all files synchronously in the transaction
          fileDataArray.forEach((fileData) => {
            const putRequest = store.put(fileData)

            putRequest.onsuccess = () => {
              completed++
              if (completed === total) {
                resolve()
              }
            }

            putRequest.onerror = () => {
              reject(new Error('Failed to store file in IndexedDB: ' + putRequest.error.message))
            }
          })

          // Handle transaction completion
          transaction.oncomplete = () => {
            if (completed === total) {
              resolve()
            }
          }

          transaction.onerror = () => {
            reject(new Error('IndexedDB transaction failed: ' + transaction.error.message))
          }
        }

        request.onupgradeneeded = () => {
          const db = request.result
          if (!db.objectStoreNames.contains('files')) {
            db.createObjectStore('files', { keyPath: 'key' })
          }
        }
      })
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },

    async handleCategoryChange() {
      this.selectedDepartment = ''
      this.selectedDocumentType = ''
      this.documentTypes = []

      if (this.selectedCategory) {
        const masterCategoryId = this.categories.find((c) => c.id === this.selectedCategory).category_id
        await this.fetchDepartmentsForCategory(masterCategoryId)
      } else {
        this.departments = []
      }

      // Save to API whenever values change
      await this.saveClassificationToAPI()
    },

    async handleDepartmentChange() {
      this.selectedDocumentType = ''

      if (this.selectedDepartment) {
        const masterCategoryId = this.categories.find((c) => c.id === this.selectedCategory).category_id
        const masterDepartmentId = this.departments.find((d) => d.id === this.selectedDepartment).department_id
        await this.fetchDocumentTypesForDepartment(masterCategoryId, masterDepartmentId)
      } else {
        this.documentTypes = []
      }

      // Save to API whenever values change
      await this.saveClassificationToAPI()
    },

    // Handle default value dropdown selection
    handleDefaultValueChange(field, selected_value) {
      // Find the field in documentTypesFields and update its selected_value
      const fieldIndex = this.documentTypesFields.findIndex((f) => f.id === field.id)
      if (fieldIndex !== -1) {
        // Create a copy of the field with the selected value
        const updatedField = { ...this.documentTypesFields[fieldIndex], selected_value }

        // Update the array with the new field data
        this.documentTypesFields.splice(fieldIndex, 1, updatedField)
      }
    },

    // Parse dropdown values from JSON string
    parseDropdownValues(dropdownValues) {
      if (!dropdownValues) return []

      try {
        const parsed = JSON.parse(dropdownValues)
        return Array.isArray(parsed) ? parsed : []
      } catch (error) {
        return []
      }
    },
  },

  watch: {
    async useAutoClassification(newValue) {
      // Save to API when checkbox changes
      await this.saveClassificationToAPI()

      // Handle switching between auto and manual classification
      if (newValue) {
        // Switching to auto classification - clear selections
        this.selectedCategory = ''
        this.selectedDepartment = ''
        this.selectedDocumentType = ''
        this.selectedDocumentTypeUploadedFilePagesCount = null
        this.selectedDocumentTypeSplitOnCode = null
        this.departments = []
        this.documentTypes = []
      } else {
        // Switching to manual classification - refetch necessary data
        if (isAiMethodBedrock() || isAiMethodTextract()) {
          // For Bedrock or Textract mode, fetch all document types directly
          await this.fetchAllDocumentTypes()
        } else {
          // For non-Bedrock/Textract mode, fetch categories to start the cascade
          await this.fetchCategories()
        }
      }
    },
    async selectedDocumentType(newValue, oldValue) {
      // Save to API when document type changes
      // Fetch document fields when document type is selected
      if (newValue) {
        // Find the selected document type and store additional information
        const selectedDocType = this.documentTypes.find((dt) => dt.id === newValue)
        if (selectedDocType) {
          this.selectedDocumentTypeUploadedFilePagesCount = selectedDocType.uploaded_file_pages_count || null
          this.selectedDocumentTypeSplitOnCode = selectedDocType.split_on_code
          this.splitByPageCount = Number(selectedDocType.page_count) || null
        } else {
          this.selectedDocumentTypeUploadedFilePagesCount = null
          this.selectedDocumentTypeSplitOnCode = null
          this.splitByPageCount = null
        }

        // Set loading state immediately before any async operations
        this.isLoadingDocumentTypesFields = true
        await this.fetchDocumentFields(newValue)

        // Validate existing files if switching to AnalyzeID
        if (this.isTextract && this.uploadedFiles.length > 0) {
          const analysisType = selectedDocType?.bda_blueprint

          if (analysisType === 'AnalyzeID') {
            await this.validateExistingFilesForAnalyzeID()
          }
        }
      } else {
        // Clear document fields when no document type is selected
        this.documentTypesFields = []
        this.selectedDocumentTypeUploadedFilePagesCount = null
        this.selectedDocumentTypeSplitOnCode = null
      }
      await this.saveClassificationToAPI()
    },
  },
}
</script>

<style scoped>
@import '@/styles/newJob.css';

.upload-progress {
  margin-top: 20px;
}

.progress-bar {
  height: 4px;
  background-color: #4caf50;
  transition: width 0.3s ease;
}

.pageContainer {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: calc(100vh - 150px);
  overflow: hidden;
}

.newJobContainer {
  display: flex;
  gap: 24px;
  /* Gap between left and right sections */
  height: 100%;
}

.uploadDivContainer {
  width: 40%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  /* Gap between upload and import sections */
  min-height: 0;
  /* Important for proper height calculation */
}

.upload-section {
  height: calc(50% - 12px);
  /* 50% minus half of the gap */
  min-height: 250px;
  /* Minimum height to look good */
  display: flex;
  flex-direction: column;
}

.uploadDivBorder {
  flex: 1;
  margin-top: 8px;
  padding: 20px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: visible;
}

.labelParagraph {
  margin: 10px 0;
  text-align: center;
}

.subPageLabel {
  display: block;
  /* margin-bottom: 4px; */
}

.clickUploadLabel {
  color: var(--primary-color);
  font-weight: 500;
  cursor: pointer;
}

.smallLabel {
  color: #666;
  margin-top: 5px;
  font-size: 12px;
}

.classification-section {
  /* background: #ffffff; */
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 20px;
  margin-bottom: 12px;
  width: 100%;
}

.classification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.auto-classification {
  display: flex;
  align-items: center;
  gap: 8px;
}

.auto-classification-checkbox {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.classification-dropdowns {
  display: flex;
  flex-direction: row;
  gap: 24px;
  width: 100%;
}

/* When only Document Type is visible (Bedrock mode), adjust layout */
.classification-dropdowns .categoryContainer:only-child {
  max-width: 300px; /* Limit width when only one dropdown is visible */
}

.categoryContainer {
  flex: 1;
  margin-bottom: 0;
}

.categoryTitle {
  font-weight: 500;
  font-size: 14px;
  color: #32393e;
  margin-bottom: 8px;
}

.categorySelect {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  color: #32393e;
  background-color: #ffffff;
}

.categorySelect:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.documentSelectedDiv {
  flex: 1;
  /* Takes remaining width */
  display: flex;
  flex-direction: column;
  background: #ffffff;
  /* border: 1px solid #e0e0e0; */
  /* border-radius: 8px; */
  /* padding: 20px; */
  height: 100%;
  overflow: hidden;
}

.pageLabel {
  font-weight: 600;
  font-size: 16px;
  color: var(--primary-color);
  margin-bottom: 8px;
  flex-shrink: 0;
  /* Prevent label from shrinking */
}

.documentDiv {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  overflow: hidden;
  position: relative;
  height: calc(100% - 40px);
}

.files-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.files-container {
  flex: 1 1 auto;
  overflow-y: auto;
  padding-right: 8px;
}

.button-container {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  /* padding: 29px 4px; */
  background: white;
  /* border-top: 1px solid #e0e0e0; */
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  gap: 12px;
  z-index: 2;
  height: 30px;
  /* margin-top: 5px; */
}

.file-item {
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 8px;
  background: white;
}

.file-item:last-child {
  margin-bottom: 8px;
  /* Keep consistent spacing */
}

.file-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-group {
  flex: 1;
}

.file-name {
  display: block;
  font-weight: 500;
}

.file-size {
  color: #666;
  font-size: 14px;
}

.remove-file {
  cursor: pointer;
  padding: 4px;
}

.error-message {
  position: absolute;
  bottom: -40px;
  left: 70%;
  transform: translateX(-50%);
  color: #dc2626;
  /* Bright red color */
  font-size: 14px;
  background-color: #fee2e2;
  /* Light red background */
  padding: 12px 24px;
  border-radius: 4px;
  white-space: nowrap;
  animation: fadeIn 0.3s ease;
  border: 1px solid #fecaca;
  /* Light red border */
  box-shadow: 0 2px 4px rgba(220, 38, 38, 0.1);
  /* Subtle red shadow */
  font-weight: 500;
  z-index: 10;
  width: max-content;
  /* Ensures text doesn't wrap */
  text-align: center;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translate(-50%, -10px);
  }

  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

/* Scrollbar styling for better visibility */
.files-container::-webkit-scrollbar {
  width: 6px;
}

.files-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.files-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.files-container::-webkit-scrollbar-thumb:hover {
  background: #666;
}

/* Button styles */
.cancel-button,
.upload-button {
  /* padding: 8px 24px; */
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  /* margin-top: 20px; */
}

.cancel-button {
  background: #ffffff;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
}

.upload-button {
  background: var(--primary-color);
  border: none;
  color: #ffffff;
}

/* Add hover effect for upload areas */
.uploadDivBorder:hover {
  border-color: var(--primary-color);
  background-color: rgba(22, 72, 99, 0.02);
}

.drag-over {
  border-color: var(--primary-color);
  background-color: rgba(22, 72, 99, 0.05);
}

.loading-indicator {
  margin-top: 4px;
  font-size: 12px;
  color: #666;
  font-style: italic;
}

/* Default Values Section Styles */
.default-values-section {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 20px;
  margin-bottom: 12px;
  width: 100%;
}

.default-values-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.default-values-dropdowns {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 24px;
  width: 100%;
}

.defaultValueContainer {
  flex: 1;
  min-width: 200px;
  margin-bottom: 0;
}

.defaultValueTitle {
  font-weight: 500;
  font-size: 14px;
  color: #32393e;
  margin-bottom: 8px;
}

.defaultValueSelect {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 14px;
  color: #32393e;
  background-color: #ffffff;
}

.defaultValueSelect:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.defaultValueSelect:focus {
  outline: none;
  border-color: var(--primary-color);
}

.default-values-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  min-height: 60px;
}

.default-values-loading .loading-indicator {
  font-size: 14px;
  color: var(--primary-color);
  font-weight: 500;
  margin-top: 0;
}
</style>
